// src/components/expert/VideoPlayer.jsx
import React, { useRef, useState } from 'react';

const PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL;

function VideoPlayer({ video, onClose }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(false);
  const [embedBlocked, setEmbedBlocked] = useState(false);

  const videoUrl = video.storagePath
    ? `${PUBLIC_URL}/${video.storagePath}`
    : null;

  const isExternal = ['youtube', 'dailymotion', 'vimeo'].includes(video.sourceType);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 2000,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '800px' }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: '12px',
        }}>
          <div style={{
            color: 'white', fontSize: '13px', fontWeight: '600',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: '80%',
          }}>
            {isExternal ? '▶ ' : '📁 '}
            {video.storagePath?.split('/').pop() || video.sourceUrl || 'Vidéo'}
          </div>
          <button
            onClick={onClose}
            style={{
              border: 'none', background: 'rgba(255,255,255,0.15)',
              color: 'white', fontSize: '18px', cursor: 'pointer',
              borderRadius: '50%', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Player */}
        {isExternal ? (
          <div>
            {!embedBlocked ? (
              <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                <iframe
                  src={getEmbedUrl(video)}
                  title="Vidéo externe"
                  style={{
                    position: 'absolute', top: 0, left: 0,
                    width: '100%', height: '100%', borderRadius: '8px',
                  }}
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  onError={() => setEmbedBlocked(true)}
                />
              </div>
            ) : (
              <div style={{
                backgroundColor: '#1a1a2e', borderRadius: '8px',
                padding: '40px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
                <div style={{ fontSize: '14px', color: 'white', marginBottom: '8px' }}>
                  Lecture intégrée non autorisée
                </div>
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '16px' }}>
                  Le propriétaire a désactivé la lecture sur des sites tiers.
                </div>
              </div>
            )}
            <a
              href={video.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '8px', marginTop: '12px', padding: '12px',
                backgroundColor: getPlatformColor(video.sourceType),
                color: 'white', fontSize: '14px', fontWeight: '600',
                textDecoration: 'none', borderRadius: '8px',
              }}
            >
              ▶ Ouvrir sur {getPlatformLabel(video.sourceType)}
            </a>
          </div>
        ) : videoUrl ? (
          error ? (
            <div style={{
              backgroundColor: '#1a1a2e', borderRadius: '8px',
              padding: '40px', textAlign: 'center', color: '#ff6b6b',
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
              <div style={{ fontSize: '14px' }}>Impossible de charger la vidéo.</div>
              <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>{videoUrl}</div>
            </div>
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              autoPlay
              onError={() => setError(true)}
              style={{
                width: '100%', borderRadius: '8px',
                backgroundColor: '#000', maxHeight: '70vh',
              }}
            />
          )
        ) : (
          <div style={{
            backgroundColor: '#1a1a2e', borderRadius: '8px',
            padding: '40px', textAlign: 'center', color: '#999',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🎬</div>
            <div style={{ fontSize: '14px' }}>Vidéo non disponible</div>
          </div>
        )}

        {/* Infos vidéo */}
        <div style={{
          display: 'flex', gap: '16px', marginTop: '12px',
          color: 'rgba(255,255,255,0.6)', fontSize: '11px', flexWrap: 'wrap',
        }}>
          {video.fileSizeMb && (
            <span>📦 {video.fileSizeMb >= 1000
              ? `${(video.fileSizeMb / 1024).toFixed(1)} GB`
              : `${video.fileSizeMb} MB`}
            </span>
          )}
          {video.durationSec && (
            <span>⏱ {Math.floor(video.durationSec / 60)}min {video.durationSec % 60}s</span>
          )}
          <span>📅 {new Date(video.createdAt).toLocaleDateString('fr-FR')}</span>
          <span style={{
            color: video.status === 'analyzed' ? '#4CAF50' :
                   video.status === 'pending'  ? '#FF9800' : '#999'
          }}>
            ● {video.status}
          </span>
        </div>
      </div>
    </div>
  );
}

function getPlatformColor(sourceType) {
  if (sourceType === 'youtube') return '#FF0000';
  if (sourceType === 'dailymotion') return '#0066DC';
  if (sourceType === 'vimeo') return '#1AB7EA';
  return '#666';
}

function getPlatformLabel(sourceType) {
  if (sourceType === 'youtube') return 'YouTube';
  if (sourceType === 'dailymotion') return 'Dailymotion';
  if (sourceType === 'vimeo') return 'Vimeo';
  return 'la plateforme';
}

function getEmbedUrl(video) {
  const url = video.sourceUrl || '';
  if (video.sourceType === 'youtube') {
    const params = new URLSearchParams(url.split('?')[1] || '');
    const id = params.get('v') || url.split('youtu.be/').pop().split('?')[0];
    return 'https://www.youtube.com/embed/' + id + '?rel=0';
  }
  if (video.sourceType === 'dailymotion') {
    const parts = url.split('/video/');
    const id = parts[1] ? parts[1].split('_')[0].split('?')[0] : '';
    return 'https://www.dailymotion.com/embed/video/' + id;
  }
  if (video.sourceType === 'vimeo') {
    const parts = url.split('vimeo.com/');
    const id = parts[1] ? parts[1].split('?')[0] : '';
    return 'https://player.vimeo.com/video/' + id;
  }
  return '';
}

export default VideoPlayer;